import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';
import { getDatabase, ref, get } from 'firebase/database';

/**
 * Service for handling eligibility data migration and gradual rollout
 * Implements Option A: Gradual Rollout with Fallback
 */

export interface MigrationStatus {
  migrated: boolean;
  migratedAt: string | null;
  programCount: number;
}

export interface MigrationStatistics {
  totalUsers: number;
  migratedUsers: number;
  skippedUsers: number;
  totalPrograms: number;
}

class EligibilityMigrationService {
  private checkUserMigrationStatusCall = httpsCallable(functions, 'checkUserMigrationStatus');
  private getMigrationStatisticsCall = httpsCallable(functions, 'getMigrationStatistics');
  private bulkExportAllUsersEligibilityCall = httpsCallable(functions, 'bulkExportAllUsersEligibility', { timeout: 600000 }); // 10 minutes timeout
  private startEligibilityRefreshCall = httpsCallable(functions, 'startEligibilityRefresh');

  /**
   * Check if the current user has been migrated to the new eligibility cache system
   */
  async checkUserMigrationStatus(): Promise<MigrationStatus> {
    try {
      const result = await this.checkUserMigrationStatusCall();
      return result.data as MigrationStatus;
    } catch (error) {
      console.error('Error checking user migration status:', error);
      return {
        migrated: false,
        migratedAt: null,
        programCount: 0
      };
    }
  }

  /**
   * Get overall migration statistics (admin only)
   */
  async getMigrationStatistics(): Promise<MigrationStatistics> {
    try {
      const result = await this.getMigrationStatisticsCall();
      return result.data as MigrationStatistics;
    } catch (error) {
      console.error('Error getting migration statistics:', error);
      throw error;
    }
  }

  /**
   * Trigger bulk migration for all users (admin only)
   */
  async triggerBulkMigration(): Promise<any> {
    try {
      const result = await this.bulkExportAllUsersEligibilityCall();
      return result.data;
    } catch (error) {
      console.error('Error triggering bulk migration:', error);
      throw error;
    }
  }

  /**
   * Get eligibility data with fallback strategy
   * This implements the gradual rollout approach:
   * 1. Try to get from proactive cache (if user is migrated)
   * 2. Fallback to local calculation (if user is not migrated)
   */
  async getEligibilityWithFallback(
    userId: string,
    programId: string,
    localCalculationFn: () => Promise<any>
  ): Promise<any> {
    try {
      // Check if user is migrated
      const migrationStatus = await this.checkUserMigrationStatus();
      
      if (migrationStatus.migrated) {
        // User is migrated - try to get from proactive cache
        console.log('✅ User is migrated, using proactive cache');
        return await this.getFromProactiveCacheWithUserId(userId, programId);
      } else {
        // User is not migrated - use local calculation
        console.log('📊 User not migrated, using local calculation');
        return await localCalculationFn();
      }
    } catch (error) {
      console.error('Error in getEligibilityWithFallback:', error);
      
      // Final fallback to local calculation
      console.log('🔄 Fallback to local calculation due to error');
      return await localCalculationFn();
    }
  }

  /**
   * Get eligibility data from proactive cache with userId
   */
  private async getFromProactiveCacheWithUserId(userId: string, programId: string): Promise<any> {
    try {
      console.log('🎯 Getting from proactive cache for program:', programId, 'user:', userId);
      
      const db = getDatabase();
      
      if (programId === 'all_programs') {
        // For all programs, we need to get all cached eligibility data
        const cacheRef = ref(db, `eligibilityCache/${userId}`);
        const snapshot = await get(cacheRef);
        
        if (snapshot.exists()) {
          const allCacheData = snapshot.val();
          console.log('✅ Retrieved all programs from proactive cache:', Object.keys(allCacheData).length, 'programs');
          return allCacheData;
        } else {
          throw new Error('No cached data found for all programs');
        }
      } else {
        // For individual program, get specific program cache
        const cacheRef = ref(db, `eligibilityCache/${userId}/${programId}`);
        const snapshot = await get(cacheRef);
        
        if (snapshot.exists()) {
          const cacheData = snapshot.val();
          console.log('✅ Retrieved from proactive cache:', cacheData);
          return cacheData;
        } else {
          throw new Error(`No cached data found for program: ${programId}`);
        }
      }
    } catch (error) {
      console.error('Error getting from proactive cache:', error);
      throw error;
    }
  }

  /**
   * Get eligibility data from proactive cache (legacy method for backward compatibility)
   */
  private async getFromProactiveCache(programId: string): Promise<any> {
    // This method is kept for backward compatibility but should not be used
    throw new Error('getFromProactiveCache without userId is deprecated - use getFromProactiveCacheWithUserId');
  }

  /**
   * Check if user should see migration progress indicators
   */
  async shouldShowMigrationProgress(): Promise<boolean> {
    try {
      const migrationStatus = await this.checkUserMigrationStatus();
      
      // Show progress if user is not migrated
      return !migrationStatus.migrated;
    } catch (error) {
      console.error('Error checking migration progress:', error);
      return false;
    }
  }

  /**
   * Get user's migration progress information
   */
  async getMigrationProgress(): Promise<{
    isMigrated: boolean;
    migratedAt: string | null;
    programCount: number;
    shouldShowProgress: boolean;
  }> {
    try {
      const migrationStatus = await this.checkUserMigrationStatus();
      
      return {
        isMigrated: migrationStatus.migrated,
        migratedAt: migrationStatus.migratedAt,
        programCount: migrationStatus.programCount,
        shouldShowProgress: !migrationStatus.migrated
      };
    } catch (error) {
      console.error('Error getting migration progress:', error);
      return {
        isMigrated: false,
        migratedAt: null,
        programCount: 0,
        shouldShowProgress: false
      };
    }
  }

  /**
   * Check if user needs migration and migrate automatically if needed
   * Returns true if user is migrated (either already or newly migrated)
   */
  async checkAndMigrateUserIfNeeded(userId: string): Promise<boolean> {
    try {
      console.log('🔄 Checking if user needs migration:', userId);
      
      // Check if user is already migrated
      const migrationStatus = await this.checkUserMigrationStatus();
      console.log('🔍 Migration status check result:', migrationStatus);
      if (migrationStatus.migrated) {
        console.log('✅ User is already migrated - skipping migration');
        return true;
      }
      
      // Check if user has sufficient data for migration
      const hasData = await this.checkUserHasEligibilityData(userId);
      if (!hasData) {
        console.log('❌ User has insufficient data for migration');
        return false;
      }
      
      // Automatically migrate the user
      console.log('🚀 Automatically migrating user:', userId);
      await this.migrateUserEligibility(userId);
      console.log('✅ User automatically migrated successfully');
      return true;
    } catch (error) {
      console.error('❌ Error in automatic migration:', error);
      return false; // Fallback to local calculation
    }
  }

  /**
   * Check if user has sufficient data for eligibility calculation
   */
  private async checkUserHasEligibilityData(userId: string): Promise<boolean> {
    try {
      const db = getDatabase();
      const userRef = ref(db, `user/${userId}`);
      const userSnapshot = await get(userRef);
      
      if (!userSnapshot.exists()) {
        return false;
      }
      
      const userData = userSnapshot.val();
      
      // Check for ProfileData
      if (!userData.ProfileData) {
        return false;
      }
      
      // Check for courses in grades object
      if (!userData.grades || Object.keys(userData.grades).length === 0) {
        return false;
      }
      
      // Check for grades in courses
      let hasAnyGrades = false;
      for (const [, course] of Object.entries(userData.grades)) {
        if (course && typeof course === 'object' && 'grades' in course && course.grades && course.grades > 0) {
          hasAnyGrades = true;
          break;
        }
      }
      
      return hasAnyGrades;
    } catch (error) {
      console.error('Error checking user data:', error);
      return false;
    }
  }

  /**
   * Migrate user eligibility data
   */
  private async migrateUserEligibility(userId: string): Promise<void> {
    try {
      // Call startEligibilityRefresh without parameters - it will use the authenticated user
      const result = await this.startEligibilityRefreshCall();
      
      if (result.data && typeof result.data === 'object' && 'success' in result.data && result.data.success) {
        console.log('✅ User migration completed successfully');
      } else {
        throw new Error('Migration failed');
      }
    } catch (error) {
      console.error('Error migrating user eligibility:', error);
      throw error;
    }
  }
}

export const eligibilityMigrationService = new EligibilityMigrationService();
